import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Star, Trash2, MapPin } from 'lucide-react';
import { getCrimeDisplayName } from '../../constants/crimes';
import './SavedSearchesModal.css';

const formatSavedAt = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const SavedSearchesModal = ({
  isOpen,
  onClose,
  savedSearches,
  onApply,
  onDelete,
  currentCriteria,
}) => {
  const displaySearches = useMemo(
    () => [...(savedSearches || [])].reverse(),
    [savedSearches]
  );

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e) => {
      if (e.key !== 'Escape') return;
      onClose();
      setTimeout(() => document.activeElement?.blur(), 0);
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const matchesCurrent = (s) =>
    currentCriteria &&
    s.locationName === currentCriteria.locationName &&
    s.locationType === currentCriteria.locationType &&
    s.crime === currentCriteria.crime &&
    String(s.dateRange) === String(currentCriteria.dateRange) &&
    s.source === currentCriteria.source;

  return createPortal(
    <div className="ss-overlay" onClick={onClose}>
      <div className="ss-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ss-header">
          <div className="ss-title">
            <Star size={18} />
            <h2>Saved Searches</h2>
          </div>
          <button type="button" className="ss-close-btn" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="ss-body">
          {displaySearches.length === 0 ? (
            <div className="ss-empty">
              <div className="ss-empty-icon">
                <Star size={36} />
              </div>
              <h3>No saved searches</h3>
              <p>Save a search from the filters to see it here.</p>
            </div>
          ) : (
            <ul className="ss-list">
              {displaySearches.map((s, displayIndex) => {
                const originalIndex = savedSearches.length - 1 - displayIndex;
                const applied = matchesCurrent(s);
                return (
                  <li key={s.id || `${s.locationName}-${displayIndex}`} className="ss-item">
                    <div className="ss-item-main">
                      <div className="ss-item-location">
                        <MapPin size={14} className="ss-item-pin" />
                        <span className="ss-item-location-name">{s.locationName}</span>
                        {s.locationType && (
                          <span className="ss-item-type-badge">{s.locationType}</span>
                        )}
                      </div>
                      <div className="ss-item-tags">
                        <span className="ss-tag ss-tag--crime">
                          {getCrimeDisplayName(s.crime, s.source)}
                        </span>
                        <span className="ss-tag ss-tag--year">{s.dateRange}</span>
                        <span className="ss-tag ss-tag--source">{s.source}</span>
                      </div>
                      {s.savedAt && (
                        <span className="ss-item-date">Saved {formatSavedAt(s.savedAt)}</span>
                      )}
                    </div>
                    <div className="ss-item-actions">
                      <button
                        type="button"
                        className={`ss-apply-btn${applied ? ' ss-apply-btn--applied' : ''}`}
                        disabled={applied}
                        onClick={() => {
                          onApply(s);
                          onClose();
                        }}
                      >
                        {applied ? 'Applied' : 'Apply'}
                      </button>
                      <button
                        type="button"
                        className="ss-delete-btn"
                        onClick={() => onDelete(originalIndex)}
                        aria-label="Delete saved search"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SavedSearchesModal;
